import React, { useEffect, useState } from "react";
import {
    Box,
    Card,
    Typography,
    Button,
    TextField,
    Container,
    Skeleton,
    IconButton,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

const editableFields = [
    { key: "fullName", label: "Full Name" },
    { key: "email", label: "Email" },
    { key: "mobileNumber", label: "Mobile Number" },
    { key: "address", label: "Address" },
];

const Profile = () => {
    const state = useSelector((state) => state);
    const participant = state.user?.participant || {};
    const navigate = useNavigate();

    const [isImageLoading, setIsImageLoading] = useState(true);
    const [editedData, setEditedData] = useState({});
    const [editingField, setEditingField] = useState(null);

    useEffect(() => {
        if (!state.user?.token) {
            navigate("/register");
        }
    }, []);

    useEffect(() => {
        const stored = localStorage.getItem("editedProfileData");
        if (stored) {
            setEditedData(JSON.parse(stored));
        } else {
            const fullName = `${participant.prefix ? participant.prefix + " " : ""}${participant.name} ${participant.middleName ? participant.middleName + " " : ""}${participant.lastName}${participant.suffix ? ", " + participant.suffix : ""}`;
            const address = participant.address
                ? `${participant.address.street}, ${participant.address.city}, ${participant.address.province}, ${participant.address.country} ${participant.address.zip}`
                : "";

            setEditedData({
                fullName,
                email: participant.email || "",
                mobileNumber: participant.mobileNumber || "",
                address,
            });
        }
    }, [participant]);

    const handleEditClick = (fieldKey) => setEditingField(fieldKey);

    const handleSaveClick = (fieldKey) => {
        setEditingField(null);
        localStorage.setItem("editedProfileData", JSON.stringify(editedData));
    };

    const handleChange = (fieldKey, value) => {
        setEditedData((prev) => ({ ...prev, [fieldKey]: value }));
    };

    const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(
        [
            "BEGIN:VCARD",
            "VERSION:3.0",
            `FN:${editedData.fullName || ""}`,
            `EMAIL:${editedData.email || ""}`,
            `TEL:${editedData.mobileNumber || ""}`,
            `ADR:;;${editedData.address || ""}`,
            "END:VCARD",
        ].join("\n")
    )}`;

    return (
        <Box sx={{ backgroundColor: "#0A66C2", minHeight: "100vh" }}>
            <Container maxWidth="sm">
                <Typography
                    sx={{ textAlign: "left", pt: 2, color: "white" }}
                    variant="h4"
                    fontWeight="bold"
                    mb={2}
                >
                    My Profile
                </Typography>

                {/* QR Code Section */}
                <Card sx={{ textAlign: "center", p: 2, mb: 3, borderRadius: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: "bold", mt: 0 }}>
                        {editedData.fullName || <Skeleton width="60%" />}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        My Shareable Code
                    </Typography>
                    <Box sx={{ position: "relative", mt: 2 }}>
                        {isImageLoading && (
                            <Skeleton
                                variant="rectangular"
                                sx={{
                                    borderRadius: 2,
                                    // position: "absolute",
                                    // top: 0,
                                    // left: "5%",
                                    width: "100%",
                                    height: 300,
                                    zIndex: 1,
                                }}
                            />
                        )}
                        <img
                            src={qrUrl}
                            alt="QR Code"
                            style={{
                                width: "90%",
                                opacity: isImageLoading ? 0 : 1,
                                transition: "opacity 0.3s ease-in-out",
                                // height: 300,
                                objectFit: "contain",
                            }}
                            onLoad={() => setIsImageLoading(false)}
                            onError={() => setIsImageLoading(false)}
                        />
                    </Box>
                </Card>
            </Container>

            {/* Editable Fields */}
            <Card sx={{ p: 2, borderRadius: 3, pb: 20 }}>
                <Typography variant="h6" gutterBottom sx={{
                    mb: 2,
                }}>
                    My Details
                </Typography>

                {editableFields.map(({ key, label }) => (
                    <Box
                        key={key}
                        sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            justifyContent: "space-between",
                            mb: 2,
                        }}
                    >
                        <Box sx={{
                            // flexGrow: 1,

                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            width: "80%",
                        }}>
                            {editingField === key ? (
                                <TextField
                                    label={label}
                                    value={editedData[key] || ""}
                                    onChange={(e) => handleChange(key, e.target.value)}
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                />
                            ) : (
                                <>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        {label}
                                    </Typography>
                                    <Typography
                                        variant="body1">{editedData[key]}</Typography>
                                </>
                            )}
                        </Box>
                        <IconButton
                            onClick={() =>
                                editingField === key
                                    ? handleSaveClick(key)
                                    : handleEditClick(key)
                            }
                            sx={{ ml: 1 }}
                        >
                            {editingField === key ? <SaveIcon /> : <EditIcon />}
                        </IconButton>
                    </Box>
                ))}
            </Card>
        </Box>
    );
};

export default Profile;
